/**
 * CV Matching Service
 * Matches CVs to ToR requirements using AI
 */

import axios, { AxiosError } from 'axios';

const CV_MATCHING_API_URL = process.env.REACT_APP_API_URL || '/api';

/** Free-text keyword evidence for a single term */
export interface FreetextTermEvidence {
    term: string;
    hitCount: number;
    snippets: Array<{
        context: string;
        position: number;
    }>;
}

/** Free-text evidence summary for a CV */
export interface FreetextEvidence {
    totalHits: number;
    boost: number;
    terms: FreetextTermEvidence[];
    matchesLogic: boolean;
    termsWithHits: number;
}

export interface CVMatch {
    cv_id: number;
    filename: string;
    associate_name: string;
    folder_name: string;
    source_location?: string;
    similarity_score: number;
    is_secondary_candidate?: boolean;  // True for secondary candidates (quick analysis)
    
    // Free-text keyword evidence (boost ranking)
    freetext_boost?: number;
    combined_score?: number;
    freetext_evidence?: FreetextEvidence | null;
    
    // Evidence fields - 9 aligned categories (V3)
    geographic_evidence?: string[];      // 1. Countries/Regions
    methodology_evidence?: string[];     // 2. Methods
    sector_evidence?: string[];          // 3. Sectors
    technical_tools_evidence?: string[]; // 4. Tools
    framework_evidence?: string[];       // 5. Frameworks
    language_evidence?: string[];        // 6. Languages
    client_type_evidence?: string[];     // 7. Donors
    education_evidence?: string[];       // 8. Education
    role_level_evidence?: string[];      // 9. Experience
    
    // Summary fields
    associate_summary?: string;
    
    // Tiered matching fields
    match_percentage?: number;
    match_breakdown?: {
        required_met: string;
        preferred_met: string;
        bonus_met: string;
    };
    viability_summary?: string;
    
    // Allocation history (previous work with IOD PARC)
    allocation_count?: number;
    has_worked_with_us?: boolean;
    allocation_summary?: Array<{
        project_number: string;
        project_name: string;
        project_client: string;
        role: string;
    }>;
    
    // Legacy fields (for backwards compatibility)
    countries?: string[];
    skills?: string[];
    languages?: string[];
    sectors?: string[];
    
    key_qualifications: string[];
    match_summary: string;
    component_scores?: {
        geographic_match: number;
        skills_match: number;
        language_match: number;
        sector_match: number;
        experience_match: number;
        education_match: number;
    };
}

/** Minimal CV info for additional matches (no LLM analysis) */
export interface AdditionalMatch {
    cv_id: number;
    filename: string;
    folder_name: string;
    source_location: string;
    similarity_score: number;
    associate_name: string;
}

/** Single criterion failure info for diagnostic feedback */
export interface CriteriaFailure {
    criterion: string;
    category: string;
    failed_count: number;
    percentage: number;
}

/** Failure analysis when no CVs pass all required criteria */
export interface FailureAnalysis {
    total_cvs_checked: number;
    criteria_failure_counts: CriteriaFailure[];
    most_blocking: string[];
    suggestion: string;
}

export interface CVMatchingResult {
    matches: CVMatch[];
    additional_matches?: AdditionalMatch[]; // CVs that passed verification but didn't get LLM analysis
    total_found: number;
    total_verified?: number; // Total CVs meeting ALL required criteria
    selected_categories?: string[]; // NEW: Categories that were searched
    search_criteria?: {
        geographic?: string;
        required_methods?: string[];
        languages?: string[];
    };
    verification_summary?: {
        total_cvs_checked: number;
        passed_all_required: number;
        required_criteria: Array<{ text: string; category: string }>;
        verification_method: string;
        note: string;
    };
    failure_analysis?: FailureAnalysis; // Diagnostic info when no CVs pass verification
    message?: string;
}

/**
 * Match CVs to manually entered requirements (OLD structure)
 * @param excludeCvIds - Optional array of CV IDs to exclude from results (for "Search for More" feature)
 * @param freetextKeywords - Optional array of free-text keywords for ranking boost
 * @param freetextLogic - Optional logic for free-text keywords ('AND' or 'OR')
 */
export async function matchCVsManual(
    requirements: any,
    topK: number = 12,
    cvSource: 'associate' | 'iod_staff' = 'associate',
    excludeCvIds?: string[],
    freetextKeywords?: string[],
    freetextLogic?: 'AND' | 'OR'
): Promise<CVMatchingResult> {
    try {
        console.log('Calling manual CV matching API:', `${CV_MATCHING_API_URL}/match-cvs-manual`);
        console.log('Manual requirements:', requirements);
        if (excludeCvIds?.length) {
            console.log('Excluding CV IDs:', excludeCvIds);
        }
        if (freetextKeywords?.length) {
            console.log('Free-text keywords:', freetextKeywords, 'Logic:', freetextLogic);
        }
        
        const response = await axios.post<CVMatchingResult>(
            `${CV_MATCHING_API_URL}/match-cvs-manual`,
            {
                requirements: requirements,
                top_k: topK,
                cv_source: cvSource,
                exclude_cv_ids: excludeCvIds,
                freetext_keywords: freetextKeywords,
                freetext_logic: freetextLogic
            },
            {
                headers: {
                    'Content-Type': 'application/json'
                },
                withCredentials: true,
                timeout: 120000
            }
        );
        
        console.log('Manual CV matching response:', response.data);
        return response.data;
    } catch (error) {
        console.error('Manual CV matching failed:', error);
        if (axios.isAxiosError(error)) {
            const axiosError = error as AxiosError;
            
            if (axiosError.code === 'ERR_NETWORK' || axiosError.code === 'ERR_CONNECTION_CLOSED') {
                throw new Error('CV matching service is not available.');
            }
            
            if (axiosError.response?.status === 401) {
                throw new Error('Authentication required. Please log in.');
            }
            if (axiosError.response?.status === 500) {
                throw new Error('CV matching service error. Please try again.');
            }
            if (axiosError.response?.status === 400) {
                throw new Error('Invalid request. Please check your requirements.');
            }
        }
        throw new Error('Failed to match CVs. Please try again.');
    }
}

/**
 * Match CVs to ToR requirements from extracted ToR (NEW structure)
 * @param excludeCvIds - Optional array of CV IDs to exclude from results (for "Search for More" feature)
 */
export async function matchCVsFromTor(
    torRequirements: any,
    queryVector?: number[],
    topK: number = 12,
    cvSource: 'associate' | 'iod_staff' = 'associate',
    excludeCvIds?: string[]
): Promise<CVMatchingResult> {
    try {
        console.log('Calling ToR CV matching API:', `${CV_MATCHING_API_URL}/match-cvs`);
        console.log('Extracted ToR requirements:', torRequirements);
        if (excludeCvIds?.length) {
            console.log('Excluding CV IDs:', excludeCvIds);
        }
        
        const response = await axios.post<CVMatchingResult>(
            `${CV_MATCHING_API_URL}/match-cvs`,
            {
                tor_requirements: torRequirements,
                query_vector: queryVector,
                top_k: topK,
                cv_source: cvSource,
                exclude_cv_ids: excludeCvIds
            },
            {
                headers: {
                    'Content-Type': 'application/json'
                },
                withCredentials: true,
                timeout: 120000
            }
        );
        
        console.log('ToR CV matching response:', response.data);
        return response.data;
    } catch (error) {
        console.error('ToR CV matching failed:', error);
        if (axios.isAxiosError(error)) {
            const axiosError = error as AxiosError;
            
            if (axiosError.code === 'ERR_NETWORK' || axiosError.code === 'ERR_CONNECTION_CLOSED') {
                throw new Error('CV matching service is not available.');
            }
            
            if (axiosError.response?.status === 401) {
                throw new Error('Authentication required. Please log in.');
            }
            if (axiosError.response?.status === 500) {
                throw new Error('CV matching service error. Please try again.');
            }
            if (axiosError.response?.status === 400) {
                throw new Error('Invalid request. This endpoint requires extracted ToR structure.');
            }
        }
        throw new Error('Failed to match CVs. Please try again.');
    }
}
